import { Loader } from '@/components/Loader';
import { useInfoByAppointment } from '@/hooks/useInfoByAppointment';
import { useInstitutionChange } from '@/hooks/useInstitutionChange';
import { PageNotFound } from '@/pages';
import {
  useAppointmentDetailsSlideStore,
  useAppointmentsStore,
} from '@/stores/appointments';
import { useInstitutionsStore } from '@/stores/institutions';
import { useToastStore } from '@/stores/toast';
import { useUsersStore } from '@/stores/users';
import {
  EDocumentManagementStatus,
  ERouterNames,
  EStatusAppointment,
  EUserRoles,
  TAppointmentCamel,
  TNullable,
} from '@/types';
import { getDocumentManagementStatus } from '@/utils/getDocumentManagementStatus';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

export const AppointmentPreJoinLayout = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const setToast = useToastStore((state) => state.setToast);

  const [isError, setIsError] = useState(false);
  const [appointment, setAppointment] =
    useState<TNullable<TAppointmentCamel>>(null);
  const getAppointment = useAppointmentsStore((state) => state.getAppointment);
  const { setOpen, setSelectedAppointment } = useAppointmentDetailsSlideStore(
    (state) => ({
      setOpen: state.setOpen,
      setSelectedAppointment: state.setSelectedAppointment,
    }),
  );
  const currentInstitutionId = useInstitutionsStore(
    (state) => state.currentInstitution?.id,
  );
  const handleInstitutionChange = useInstitutionChange();

  const { checkRole, checkRoleIds } = useUsersStore((state) => ({
    checkRole: state.checkRole,
    checkRoleIds: state.checkRoleIds,
  }));

  useEffect(() => {
    const callback = async () => {
      const appointmentId = params.appointmentId;
      const appointment = appointmentId
        ? await getAppointment(appointmentId)
        : undefined;
      if (!appointment) {
        setIsError(true);
        return;
      }
      if (appointment.institutionId !== currentInstitutionId) {
        handleInstitutionChange(
          appointment.institutionId,
          window.location.pathname + window.location.search,
        );
        return;
      }
      setAppointment(appointment);
    };
    void callback();
  }, [
    currentInstitutionId,
    getAppointment,
    handleInstitutionChange,
    params.appointmentId,
  ]);

  const { lastStatus, isAppointmentEnded, startTime, isCanceled } =
    useInfoByAppointment(appointment);

  const navigateToSession = useCallback(() => {
    if (appointment?.waitingRoom?.id && !appointment.waitingRoom.endedAt) {
      navigate(
        generatePath(`/${ERouterNames.WAITING_ROOM}`, {
          waitingRoomId: appointment?.waitingRoom.id ?? '',
        }),
      );
      return;
    }
    const isVideo = appointment?.communicationChannel === 'video';
    const encounterUrl = generatePath(`/${ERouterNames.ENCOUNTER}`, {
      chatId: appointment?.chatId ?? '',
    });
    const chatUrl = generatePath(`/${ERouterNames.CHAT}`, {
      chatId: appointment?.chatId ?? '',
    });
    navigate(isVideo ? encounterUrl : chatUrl);
  }, [
    appointment?.chatId,
    appointment?.communicationChannel,
    appointment?.waitingRoom?.endedAt,
    appointment?.waitingRoom?.id,
    navigate,
  ]);

  useEffect(() => {
    if (!appointment) return;
    const isParticipant = checkRoleIds(
      appointment.participants.map(
        ({ userInstitutionRoleId }) => userInstitutionRoleId,
      ),
    );
    if (!isParticipant && checkRole(EUserRoles.COORDINATOR)) {
      navigate(ERouterNames.COMMAND_CENTER);
      return;
    }
    const redirectToSessionDetails = (
      redirectToPastSession: boolean = false,
    ) => {
      setOpen(true);
      setSelectedAppointment(appointment);
      navigate(
        !redirectToPastSession ? ERouterNames.DASHBOARD : ERouterNames.SESSIONS,
      );
    };
    if (isAppointmentEnded && !isCanceled) {
      redirectToSessionDetails(!checkRole(EUserRoles.PATIENT));
      setToast({
        variant: 'info',
        title: t('appointment-pre-join.the-appointment-is-over'),
      });
      return;
    }
    const isConfirmed = lastStatus?.action === EStatusAppointment.CONFIRMED;
    const isLessThanOneHourBeforeStart = dayjs().isAfter(
      dayjs(startTime).subtract(1, 'hour'),
    );
    const patientRequiredEntries = appointment?.formEntries.filter(
      (entry) =>
        getDocumentManagementStatus(entry) ===
          EDocumentManagementStatus.NOT_COMPLETED &&
        entry.data?.required &&
        entry.formTemplate.userType === 'patient',
    );

    if (isConfirmed && !patientRequiredEntries.length) {
      if (isLessThanOneHourBeforeStart) {
        navigateToSession();
      } else {
        redirectToSessionDetails();
        setToast({
          variant: 'info',
          title: t('appointment-pre-join.the-appointment-has-not-started-yet'),
        });
      }
      return;
    }

    redirectToSessionDetails();
  }, [
    startTime,
    isCanceled,
    appointment,
    isAppointmentEnded,
    lastStatus?.action,
    checkRole,
    checkRoleIds,
    navigate,
    navigateToSession,
    setOpen,
    setSelectedAppointment,
    setToast,
    t,
  ]);

  if (isError) return <PageNotFound />;
  return <Loader full />;
};
